import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { FirstDibsEntryDtoWindowTypeEnum } from '@b-stock/listing-api-client'

import { AuctionState } from '@/buyer-portal/components/features/listing/actions/auction/const'
import { getAuctionState } from '@/buyer-portal/components/features/listing/actions/auction/useAuctionDetailState'
import type { PhaseSource } from '@/buyer-portal/helpers/auctions/buyingFormat'
import { toFirstDibs } from '@/components/listings/listingCard/BaseListingCard/ListingInfo/useCardFirstDibsWindow'
import { FeatureFlags } from '@/helpers/featureFlags'
import { nextInstant, useRerenderAt } from '@/hooks/useRerenderAt'
import type { FirstDibsGroup } from '@/queries/listingDetailsQuery'

import {
  getFdActiveGroup,
  getFdEarlyPrice,
  getFdWindowMs,
} from './useFdActiveGroup'

// A search result whose 3MP auction didn't load.
const noAuction = { canceled: false, closed: false }

/**
 * Whether this First Dibs group grants early Buy Now access.
 * Uses windowType when present; falls back to earlyPrice presence on surfaces
 * (e.g. search cards) where the index does not carry windowType.
 */
const groupHasBuyNow = (group: FirstDibsGroup): boolean => {
  if (group.windowType) {
    return (
      group.windowType === FirstDibsEntryDtoWindowTypeEnum.BuyNow ||
      group.windowType === FirstDibsEntryDtoWindowTypeEnum.Both
    )
  }
  return getFdEarlyPrice(group) !== undefined
}

export type FirstDibsPurchase = {
  fdActive: boolean
  /** The buyer's open group while First Dibs applies. */
  activeGroup: FirstDibsGroup | undefined
  /** Sent with bids and buy-now while First Dibs applies. */
  buyerGroupId: string | undefined
  /**
   * True when the active group allows early Buy Now (windowType BUY_NOW or
   * BOTH) and there is a price to charge. The card switches to the BINO
   * variant when this is true.
   */
  fdHasBuyNow: boolean
  /**
   * Early Buy Now price in dollars. Set when fdHasBuyNow is true:
   * the group's own earlyPrice if discounted, otherwise the auction's
   * regular Buy Now price (no discount — same price as after the window).
   */
  earlyPrice: number | undefined
}

const getAuctionAndFirstDibs = (listing: PhaseSource) =>
  'auction3MP' in listing
    ? ([listing.auction3MP, toFirstDibs(listing.firstDibs)] as const)
    : ([listing.auction, listing.firstDibs] as const)

/** The instants at which First Dibs can start or stop applying. */
export const getFirstDibsBoundaries = (listing: PhaseSource) => {
  const [auction, firstDibs] = getAuctionAndFirstDibs(listing)
  return [
    ...(firstDibs?.groups ?? []).flatMap((group) => {
      const window = getFdWindowMs(group)
      return window ? [window.startMs, window.endMs] : []
    }),
    Date.parse(auction?.startTime ?? ''),
  ]
}

/**
 * The one answer to "does First Dibs apply to this purchase right now?",
 * shared by the bid card, the confirmation modals and the bid/buy-now
 * requests so what the buyer sees is what gets sent.
 *
 * First Dibs applies while the buyer's group window is open AND the auction
 * itself is still Scheduled — once the auction is live, canceled or ended,
 * the regular flow takes over.
 */
export const getFirstDibsPurchase = ({
  listing,
  enabled,
  now,
}: {
  listing: PhaseSource
  enabled: boolean
  now: number
}): FirstDibsPurchase => {
  const [auction, firstDibs] = getAuctionAndFirstDibs(listing)
  const activeGroup = enabled ? getFdActiveGroup(firstDibs, now) : undefined
  const fdActive =
    !!activeGroup &&
    getAuctionState(auction ?? noAuction, now).auctionState ===
      AuctionState.Scheduled
  // For BUY_NOW/BOTH groups, fall back to the regular Buy Now price when the
  // group carries no earlyPrice (buyer gets early access at the normal price).
  const earlyPrice =
    fdActive && groupHasBuyNow(activeGroup)
      ? (getFdEarlyPrice(activeGroup) ??
        (auction?.buyNow?.enabled
          ? (auction.buyNow.price ?? undefined)
          : undefined))
      : undefined
  // No price to charge means no Buy Now variant, even for a BUY_NOW/BOTH group.
  const fdHasBuyNow = earlyPrice !== undefined

  return {
    fdActive,
    activeGroup: fdActive ? activeGroup : undefined,
    buyerGroupId: fdActive ? activeGroup.buyerGroupId : undefined,
    fdHasBuyNow,
    earlyPrice,
  }
}

/** `getFirstDibsPurchase` for this buyer, re-rendering as First Dibs starts or stops applying. */
export function useFirstDibsPurchase(listing: PhaseSource): FirstDibsPurchase {
  const enabled = useFlag(FeatureFlags.ENABLE_FIRST_DIBS)
  const now = Date.now() // oxlint-disable-line react/purity
  useRerenderAt(nextInstant(getFirstDibsBoundaries(listing), now))
  return getFirstDibsPurchase({ listing, enabled, now })
}
